import { axiosClient } from "../api/axiosClient";
import type { ActivityType } from "../types/activity.types";

/** Same object events already send. The detail response still exposes `location` as text. */
export type ActivityLocationPayload = {
  isInTerreiro: boolean;
  knownLocation?: string;
  street?: string;
  number?: string;
  complement?: string;
  neighborhood?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  reference?: string;
};

export type ActivityCreatePayload = {
  type?: ActivityType;
  title: string;
  terreiroId?: string;
  mediumId?: string;
  description: string;
  date: string;
  startTime: string;
  endTime: string;
  isTemplate: boolean;
  isAllDay: boolean;
  minConfirmedMembers?: number | null;
  insumos: Array<{ id: string; quantity: number }>;
  members: string[];
  qrReleaseOffsetMinutes?: number;
  qrCheckinGraceMinutes?: number;
  qrCheckinEnabled?: boolean;
  isInTerreiro?: boolean;
  location?: ActivityLocationPayload | null;
};
export type ActivityModelCreatePayload = {
  type?: ActivityType;
  terreiroId?: string;
  mediumId?: string;
  title: string;
  description: string;
  isTemplate: boolean;
  isAllDay: boolean;
  minConfirmedMembers?: number | null;
  insumos: Array<{ id: string; quantity: number }>;
  members: string[];
  qrReleaseOffsetMinutes?: number;
  qrCheckinGraceMinutes?: number;
  qrCheckinEnabled?: boolean;
  isInTerreiro?: boolean;
  location?: ActivityLocationPayload | null;
};

export type ActivityModelUpdatePayload = ActivityModelCreatePayload & {
  id: string;
};

export type ActivityUpdatePayload = ActivityCreatePayload & {
  id?: string;
  terreiroId?: string;
  mediumId?: string;
};

export type ActivityBulkStatusUpdatePayload = {
  activityIds: string[];
  status: string;
};

export const activitiesRepository = {
  getRecentActivities: async (terreiroId?: string) => {
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 800));

    const allActivities = [
      {
        id: 1,
        title: "Novo médium cadastrado",
        type: "member" as const,
        description: "Maria Santos foi adicionada ao terreiro",
        timestamp: new Date(Date.now() - 30 * 60 * 1000), // 30 minutes ago
        icon: "👥",
        terreiroId: "terreiro-1",
      },
      {
        id: 2,
        title: "Evento criado",
        type: "event" as const,
        description: "Gira de Oxalá foi agendada para hoje",
        timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000), // 2 hours ago
        icon: "📅",
        terreiroId: "terreiro-2",
      },
      {
        id: 3,
        title: "Contribuição",
        type: "financial" as const,
        description: "Contribuição mensal de R$ 50,00",
        timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000), // 4 hours ago
        icon: "💰",
        terreiroId: "terreiro-1",
      },
      {
        id: 4,
        title: "Item adicionado ao inventário",
        type: "inventory" as const,
        description: "10 velas brancas foram adicionadas",
        timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000), // 1 day ago
        icon: "📦",
        terreiroId: "terreiro-2",
      },
      {
        id: 5,
        title: "Banho pessoal concluído",
        type: "event" as const,
        description: "Seu ritual pessoal foi registrado no histórico",
        timestamp: new Date(Date.now() - 90 * 60 * 1000),
        icon: "🛁",
      },
    ];

    if (terreiroId) {
      return allActivities.filter(
        (activity) => activity.terreiroId === terreiroId,
      );
    }

    return allActivities.filter((activity) => !activity.terreiroId);
  },
  createActivity: async (payload: ActivityCreatePayload) => {
    const response = await axiosClient.post("/activity", payload, {
      withCredentials: true,
    });
    return response.data;
  },
  updateActivity: async (id: string, payload: ActivityUpdatePayload) => {
    const response = await axiosClient.put(`/activity/${id}`, payload, {
      withCredentials: true,
    });
    return response.data;
  },
  updateActivityStatus: async (activityId: string, status: string) => {
    const response = await axiosClient.patch(
      `/activity/${activityId}/status`,
      { status },
      { withCredentials: true },
    );
    return response.data;
  },
  bulkUpdateActivityStatus: async (
    payload: ActivityBulkStatusUpdatePayload,
  ) => {
    const response = await axiosClient.patch("/activity/status/bulk", payload, {
      withCredentials: true,
    });
    return response.data;
  },
  createActivityModel: async (payload: ActivityModelCreatePayload) => {
    const response = await axiosClient.post("/activity/model", payload, {
      withCredentials: true,
    });
    return response.data;
  },
  updateActivityModel: async (payload: ActivityModelUpdatePayload) => {
    const response = await axiosClient.put(
      `/activity/model/${payload.id}`,
      payload,
      {
        withCredentials: true,
      },
    );
    return response.data;
  },
};
